import { Divider, Stack, Text } from '@chakra-ui/react';

import ConfirmationModal from '@/client/components/admin/shared/overlay/confirmation-modals/ConfirmationModal';
import type { ExpertFormData } from './types';
import LinearGradientBorderButton from '@/client/components/shared/buttons/LinearGradientBorderButton';
import SegmentedButtonControl from '@/client/components/shared/buttons/SegmentedButtonControl';
import { useAIAgentStore } from '../../../../ai-agent/v2/AIAgentStore';
import { useFormContext } from 'react-hook-form';
import { useGetPublicImage } from '@/client/services/hooks/media/useGetPublicImage';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateExpert } from '../hooks/useUpdateExpert';

export default function ExpertPreview() {
  const { t } = useTranslation();
  const { getPublicImage } = useGetPublicImage();
  const { updateExpert } = useUpdateExpert();
  const { setEddyDrawerIsOpen, setPreviewExpert, setActiveTab, setCurrentConversationId } =
    useAIAgentStore();
  const { watch, setValue, getValues } = useFormContext<ExpertFormData>();
  const { id, name, color, enabled } = watch();

  const [disableModalIsOpen, setDisableModalIsOpen] = useState(false);
  const [publishModalIsOpen, setPublishModalIsOpen] = useState(false);

  const handleDisable = () => {
    setValue('enabled', false, { shouldDirty: true, shouldTouch: true, shouldValidate: true });

    if (id) {
      updateExpert.mutate({
        expertId: id,
        formData: getValues(),
      });
    }
  };

  const handleEnable = () => {
    // Switching from false to true - update form value normally
    setValue('enabled', true, { shouldDirty: true, shouldTouch: true, shouldValidate: true });

    if (id) {
      updateExpert.mutate({
        expertId: id,
        formData: getValues(),
      });
    }
  };

  const handleEnabledChange = (checked: boolean) => {
    if (!checked && enabled) {
      // Switching from true to false - call function without setting form value
      setDisableModalIsOpen(true);
    } else {
      setPublishModalIsOpen(true);
    }
  };

  return (
    <>
      <Stack spacing={6} paddingTop={8}>
        <Stack spacing={4}>
          <SegmentedButtonControl
            leftButton={{
              label: t('admin.aiAgent.experts.form.preview.draft'),
              onClick: () => handleEnabledChange(false),
              isSelected: !enabled,
            }}
            rightButton={{
              label: t('admin.aiAgent.experts.form.preview.published'),
              onClick: () => handleEnabledChange(true),
              isSelected: enabled,
            }}
          />

          <Text fontSize="12px" fontWeight={500} color="neutral.600">
            {enabled
              ? t('admin.aiAgent.experts.form.preview.published.helpText')
              : t('admin.aiAgent.experts.form.preview.draft.helpText')}
          </Text>
        </Stack>

        <Divider borderColor="neutral.100" />
        {id && (
          <LinearGradientBorderButton
            onClick={() => {
              setPreviewExpert({
                id,
                name,
                color,
              });
              setCurrentConversationId('');
              setActiveTab('conversation');

              setEddyDrawerIsOpen(true);
            }}
          >
            <Text background="linear-gradient( #FC9AB4, #D860F2)" backgroundClip="text">
              {t('admin.aiAgent.experts.form.preview.testThisExpert')}
            </Text>
          </LinearGradientBorderButton>
        )}
      </Stack>

      <ConfirmationModal
        isOpen={disableModalIsOpen}
        onClose={() => setDisableModalIsOpen(false)}
        imageSrc={getPublicImage('continu/app-images/add-files-E-commerce.svg')}
        title={t('admin.aiAgent.experts.form.preview.unpublishModal.title')}
        descriptionOne={t('admin.aiAgent.experts.form.preview.unpublishModal.descriptionOne')}
        submitButtonOptions={{
          label: t('admin.aiAgent.experts.form.preview.unpublishModal.submitButtonLabel'),
          onClick: () => {
            handleDisable();
            setDisableModalIsOpen(false);
          },
        }}
      />

      <ConfirmationModal
        isOpen={publishModalIsOpen}
        onClose={() => setPublishModalIsOpen(false)}
        imageSrc={getPublicImage('continu/app-images/Approval-2--Streamline-Brooklyn.svg')}
        title={t('admin.aiAgent.experts.publishModal.title')}
        descriptionOne={t('admin.aiAgent.experts.publishModal.descriptionOne')}
        submitButtonOptions={{
          label: t('admin.aiAgent.experts.publishModal.submitButtonLabel'),
          onClick: () => {
            handleEnable();
            setPublishModalIsOpen(false);
          },
        }}
      />
    </>
  );
}
